import { useRef, useState } from 'react'
import { useVODStore } from '@/shared/store'
import { useVODService } from '@/app/context/DIContext/DIServices'
import { selectSetVodRowItems } from '@/shared/store/storeSelectors/vodSelectors'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import { IVODServiceStreamIQ } from '@/shared/types/services/types/vodService/IVodServiceStreamIQ'
import { filterDuplicates, getErrorMessage } from '@/shared/lib'
import { ApiListItem } from '@/clients/streamiq/services/containers/VODService/VODServiceStreamIQ'

export function useVodItemsStreamIQ(
    id: string | number,
    type: string,
    serviceName: string,
    url?: string,
    isCardOrientationPriorityHorizontal?: boolean
) {
    const setVodRowItems = useVODStore(selectSetVodRowItems)
    const { getVodItems, createId, fetchVodItemsFromList } =
        useVODService() as IVODServiceStreamIQ

    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const [recommendationItems, setRecommendationItems] = useState<VodItem[]>(
        []
    )
    const vodItemsToAdd = 10
    const startItemIndex = useRef(0)

    const rowItemsRef = useRef<VodItem[]>([])

    const { fetchRecommendationList } = useVODService() as IVODServiceStreamIQ

    const getRecommendationItems = async (): Promise<void> => {
        rowItemsRef.current = []

        try {
            const response = await fetchRecommendationList(
                type,
                String(id),
                serviceName
            )
            if (response?.length) {
                setRecommendationItems(response)
            }
        } catch (error) {
            getErrorMessage('fetchVodRows', error)
        }
    }

    const getItemsFromList = async (
        listItems: ApiListItem[],
        rowId?: string
    ): Promise<VodItem[]> => {
        setIsLoading(true)

        const responseId = createId(rowId || id)

        try {
            setIsError(false)
            const response = await fetchVodItemsFromList(
                listItems,
                isCardOrientationPriorityHorizontal
            )

            if (response.length) {
                const newData = filterDuplicates(
                    [...rowItemsRef.current, ...response],
                    'id'
                )

                rowItemsRef.current = newData
                setVodRowItems(responseId, newData)

                return rowItemsRef.current
            }

            return []
        } catch (error) {
            setIsError(true)
            setIsLoading(false)
            getErrorMessage('useVodItemsFromList', error)
        } finally {
            setIsLoading(false)
        }

        return []
    }

    const getItems = async (
        rowId?: string,
        query = url
    ): Promise<VodItem[]> => {
        setIsLoading(true)

        const queryParams = {
            page: startItemIndex.current,
            pageSize: vodItemsToAdd,
            resolveReferences: true,
        }

        startItemIndex.current += 1

        const responseId = createId(rowId || id)

        try {
            setIsError(false)
            const response = await getVodItems(
                responseId,
                query,
                queryParams,
                isCardOrientationPriorityHorizontal
            )
            // console.log('vod items', response, query)
            if (response.length) {
                const newData = filterDuplicates(
                    [...rowItemsRef.current, ...response],
                    'id'
                )

                rowItemsRef.current = newData
                setVodRowItems(responseId, newData)

                return rowItemsRef.current
            }

            return []
        } catch (error) {
            setIsError(true)
            setIsLoading(false)
            getErrorMessage('useVodItems', error)
        } finally {
            setIsLoading(false)
        }

        return []
    }

    const rowItems = rowItemsRef.current

    const setRowItems = (items: VodItem[]) => (rowItemsRef.current = items)

    return {
        rowItems,
        recommendationItems,
        getItems,
        getItemsFromList,
        setRowItems,
        isLoading,
        isError,
        getRecommendationItems,
    }
}
